import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../utils/apiClient';
import { notifyFailure } from '../utils/toast';
import { RegistrationConfirmation } from '../types/RegistrationConfirmation.type';

interface RegistrationConfirmationResponse {
  status: number;
  message: string;
  data: RegistrationConfirmation;
}

export const useRegistrationConfirmation = (
  registrationId: number | null,
  enabled: boolean,
) => {
  const [confirmation, setConfirmation] =
    useState<RegistrationConfirmation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfirmation = useCallback(async () => {
    if (!enabled || !registrationId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<RegistrationConfirmationResponse>(
        `/registration/confirmation/${registrationId}`,
      );
      setConfirmation(response.data);
    } catch (requestError) {
      console.error('Failed to load registration confirmation:', requestError);
      setConfirmation(null);
      setError('Failed to load registration confirmation.');
      notifyFailure();
    } finally {
      setLoading(false);
    }
  }, [enabled, registrationId]);

  useEffect(() => {
    if (enabled && registrationId) {
      loadConfirmation();
    } else {
      setConfirmation(null);
      setError(null);
      setLoading(false);
    }
  }, [enabled, registrationId, loadConfirmation]);

  return {
    confirmation,
    loading,
    error,
    reload: loadConfirmation,
  };
};
